
import { useState, useEffect, memo } from "react";
import { ArrowRight, Shield, Clock, MessageSquare, Calculator, TrendingDown, Users, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import OptimizedImage from "@/components/shared/OptimizedImage";
import { Link } from "react-router-dom";
import { trackEvent, EVENTS } from "@/utils/analytics-events";

const MarketAlertBadge = memo(() => (
  <div className="inline-flex items-center gap-2 bg-destructive/10 border border-destructive/20 rounded-full py-1.5 px-4 mb-4 animate-fade-in">
    <TrendingDown size={14} className="text-destructive" />
    <span className="text-xs font-semibold text-destructive">
      Tỷ suất cho thuê HCM chỉ còn 2.2% — thấp hơn gửi tiết kiệm
    </span>
  </div>
));

const SocialProofBar = memo(() => (
  <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t border-border/50">
    <div className="flex items-center gap-2">
      <div className="flex -space-x-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="w-7 h-7 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center">
            <Users size={12} className="text-primary" />
          </div>
        ))}
      </div>
      <span className="text-sm text-muted-foreground font-medium">Hàng chục chủ nhà tin tưởng</span>
    </div>
    <div className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground">
      <Home size={14} className="text-[var(--theme-accent)]" />
      <span className="font-medium">Hàng chục căn hộ đang quản lý tại TP.HCM</span>
    </div>
  </div>
));

const ValuePropositions = memo(() => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-24 mb-6">
    <ValueCard
      icon={<Shield size={22} className="text-white" />}
      title="Phí Chỉ Từ 8%"
      description="Phí quản lý minh bạch, chỉ tính trên tiền thuê thực thu — không phí ẩn, không rủi ro"
      highlight
    />
    <ValueCard 
      icon={<TrendingDown size={22} className="text-white" />}
      title="Tối Ưu Lợi Nhuận"
      description="Tăng thu nhập ròng cho thuê lên đến 40% so với tự quản lý — trong bối cảnh tỷ suất cho thuê giảm sâu"
    />
    <ValueCard 
      icon={<Clock size={22} className="text-white" />}
      title="Không Phải Lo Gì"
      description="Từ tìm khách, sàng lọc, hợp đồng đến bảo trì — chúng tôi lo trọn gói, bạn chỉ cần nhận tiền"
    />
  </div>
));

const ValueCard = ({ icon, title, description, highlight = false }: { icon: React.ReactNode; title: string; description: string; highlight?: boolean }) => (
  <div className={`flex items-start p-5 rounded-lg shadow-sm border transition-all hover:shadow-md ${
    highlight 
      ? 'bg-primary/5 border-primary/20 ring-1 ring-primary/10' 
      : 'bg-card/90 border-border'
  }`}>
    <div className="w-12 h-12 rounded-full bg-[var(--theme-accent)] flex items-center justify-center mr-4 mt-1 shrink-0 shadow-sm">
      {icon}
    </div>
    <div>
      <p className="font-medium theme-primary-text text-lg">{title}</p>
      <p className="text-base text-muted-foreground mt-1">{description}</p>
    </div>
  </div>
);

const NotificationCard = memo(() => (
  <Card className="bg-muted/95 backdrop-blur-sm border-0 shadow-lg rounded-xl overflow-hidden">
    <CardContent className="p-0">
      <div className="bg-[var(--theme-primary)] px-3 py-1.5 flex items-center">
        <MessageSquare size={14} className="text-white mr-1.5" />
          <p className="text-white text-xs font-medium">Propico</p>
        </div>
      
      <div className="p-3">
        <div className="bg-card rounded-lg p-2.5 shadow-sm border-l-3 border-[var(--theme-accent)]">
          <p className="text-xs text-foreground font-medium">Tin tốt!</p>
          <p className="text-base font-bold text-[var(--theme-accent)]">15.000.000đ<span className="text-xs font-medium">/tháng</span></p>
          <div className="flex items-center mt-1 text-xs text-muted-foreground">
            <Clock size={10} className="mr-1 text-[var(--theme-primary)]" />
            <p className="text-[10px]">Dịch vụ quản lý bắt đầu trong 21 ngày</p>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
));

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    return () => {
      setIsVisible(false);
    };
  }, []);

  const scrollToPropertyForm = () => {
    const element = document.getElementById('property-inquiry-form');
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative pt-20 pb-10 md:pt-28 md:pb-14 overflow-hidden theme-hero" id="property-inquiry">
      <div className="absolute top-0 right-0 w-1/2 h-full theme-gradient -z-10"></div>
      
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className={`space-y-5 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div>
              <MarketAlertBadge />
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight theme-primary-text">
                Cho Thuê Căn Hộ An Nhàn — Quản Lý Chuyên Nghiệp Từ A Đến Z
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mt-4">
                Propico quản lý trọn gói từ tìm khách, ký hợp đồng, thu tiền đến bảo trì — bạn chỉ cần nhận báo cáo và tiền thuê hàng tháng.
              </p>
              <div className="h-1 w-20 my-4 bg-[var(--theme-accent)] rounded-full opacity-80"></div>
            </div>
            
            <p className="text-lg text-muted-foreground max-w-lg">
              Dịch vụ quản lý cho thuê chuyên nghiệp tại Việt Nam với phí chỉ từ 8% tiền thuê thực thu — an tâm tuyệt đối, minh bạch hoàn toàn.
            </p>
            
            <div className="space-y-3 pt-1">
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="text-base group bg-[var(--theme-primary)] hover:bg-[var(--theme-primary)]/90"
                  size="lg"
                  variant="default"
                  onClick={() => {
                    trackEvent(EVENTS.CTA_CLICK, { location: 'hero_primary', text: 'Nhận Báo Giá Miễn Phí' });
                    scrollToPropertyForm();
                  }}
                >
                  Nhận Báo Giá Miễn Phí
                  <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                
                <Link to="/rent-predictor" onClick={() => {
                  trackEvent(EVENTS.CTA_CLICK, { location: 'hero_secondary', text: 'Ước Tính Giá Thuê' });
                }}>
                  <Button 
                    className="text-base group"
                    size="lg"
                    variant="secondary"
                  >
                    Ước Tính Giá Thuê
                    <Calculator size={18} className="ml-2" />
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-muted-foreground font-medium">
                ⏱️ Chỉ mất 2 phút • Phản hồi trong 24–48 giờ • Đánh giá miễn phí.
              </p>
            </div>

            <SocialProofBar />
          </div>

          <div className={`relative ${isVisible ? 'animate-fade-in' : 'opacity-0'} animation-delay-300 -mb-16`}>
            <div className="relative z-10">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=60"
                  alt="Căn hộ cao cấp tại TP.HCM được Propico quản lý chuyên nghiệp cho chủ nhà"
                  className="w-full h-auto object-cover"
                  width={600}
                  height={400}
                  priority
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                
                <div className="absolute bottom-6 right-6 max-w-[220px] transform translate-y-0 hover:translate-y-[-5px] transition-all duration-300">
                  <NotificationCard />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <ValuePropositions />

      </div>
    </section>
  );
};

export default Hero;
